import React, { useRef, useState } from 'react';
import { SignUpBtn, StatusMessage } from '../components/ui';
import { useFormValidation } from '../hooks/useFormValidation';
import { validators } from '../helpers/validators';
import { FormErrorSummary } from '../components/ui/FormErrorSummary';
import { apiService } from '../services';

interface SignupFormProps {
    isInsideModal?: boolean;
    onSuccess?: () => void;
    onValidationError?: () => void;
}

const SignupForm: React.FC<SignupFormProps> = ({ isInsideModal = false, onSuccess,onValidationError }) => {

    const [showForm, setShowForm] = useState(true);
    const [showErrorSummary, setShowErrorSummary] = useState(false);
    const [showResponceErrorSummary, setShowResponceErrorSummary] = useState(false);
    const [responceError, setResponceError] = useState<string | null>(null);

    type FormData = {
        reason: string;
    };

    const [formData, setFormData] = useState<FormData>({
        reason: ""
    });

    const lastSubmitRef = useRef<number>(0);
    const SIGNUP_THROTTLE_MS = 1000;

    const { values, errors, handleChangeFormField, isValid = false, validateAll } =
        useFormValidation({
            initialValues: [formData],
            rules: {
                reason: [
                    validators.required("Reason is required"),
                    validators.minLength(20)
                ],
            }
        });

    const handleSignUpSuccess = async () => {

        const now = Date.now();
        if (now - lastSubmitRef.current < SIGNUP_THROTTLE_MS) {
             setTimeout(() => {
              setShowResponceErrorSummary(false);
              onValidationError?.();
            }, 1000);
            setShowResponceErrorSummary(true);
            setResponceError("Too many requests. Please wait a moment.");
            return;
        }
        lastSubmitRef.current = now;

        const valid = validateAll([formData]);

        let hasErrors = false;

        if (Array.isArray(valid)) {
            hasErrors = valid.some(v => v && Object.keys(v).length > 0);
        } else if (valid && typeof valid === 'object') {
            hasErrors = Object.keys(valid).length > 0;
        }
        else{
            console.info('Unexpected validation result:', valid);
        }

        if (hasErrors) {
            setShowErrorSummary(true);
            setShowResponceErrorSummary(false);
            onValidationError?.();
            return;
        }

        try {
            const response = await apiService.accessRequest(
                formData.reason
            );
            if (response.success) {
                setShowForm(false);
                onValidationError?.();
                if (isInsideModal && onSuccess) {
                    onSuccess();
                }
            }
            else {
                onValidationError?.();
                setShowResponceErrorSummary(true);
                setResponceError("Something went wrong");
            }
        } catch (err) {
            console.info('Error :', err);
        }
    };

    const handleCloseStatusMessage = () => {
        setShowErrorSummary(false);
    };

    const handleCloseResponceStatusMessage = () => {
        setShowResponceErrorSummary(false);
    };

    const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        handleChangeFormField(0, "reason")(e);
        setFormData({ ...formData, reason: e.target.value });
    };

    // Remove handleChange, only handleReasonChange is needed

    return (
        <div className='login__sign-up'>
            {showErrorSummary && [
                ...new Set(
                    errors.flatMap(err => Object.values(err).filter(Boolean))
                )
            ].length > 0 && (
                    <StatusMessage kind="error" onClose={handleCloseStatusMessage}>
                        <FormErrorSummary
                            errors={[
                                ...new Set(
                                    errors.flatMap(err => Object.values(err).filter(Boolean))
                                )
                            ]}
                        />
                    </StatusMessage>
                )}
            {responceError && showResponceErrorSummary && (
                    <StatusMessage kind="error" onClose={handleCloseResponceStatusMessage}>
                         {responceError}
                    </StatusMessage>
            )}
            {showForm ? (
                <form onSubmit={e => {
                    e.preventDefault();
                    if (validateAll()) {
                        setFormData({
                            reason: values[0]?.reason
                        });
                        handleSignUpSuccess();
                    } else {
                        setShowErrorSummary(true);
                    }
                }}>
                    <h2 className='mt-0'>Request Access</h2>
                    <span>Your Reason:</span>
                    <textarea
                        placeholder='Your Reason'
                        className={['mb-2', errors[0]?.reason && 'textarea--error'].filter(Boolean).join(' ')}
                        value={formData.reason || ""}
                        onChange={handleReasonChange}
                    />
                    {!isInsideModal && <SignUpBtn onSuccess={handleSignUpSuccess} />}
                </form>
            ) : (
                <div className="message">
                    <h2 className='mt-0'>Thank You!</h2>
                    <p>Thank you, your details have been submitted and we will review shortly. Once you have been added to the system please use the login to the left to gain access to the system.</p>
                    <p>We will normally respond to your request within a 2hr period but please allow at least 24hrs before responding back.</p>
                </div>
            )}
        </div>
    );
};

export default SignupForm;
